<template>
    <v-dialog v-model="dialogShow" max-width="600px">
        <template v-slot:activator="{ on }">
            <v-btn color="primary" dark class="mb-2 primary ml-2" v-on="on" @click="openDialog()"
                :disabled="seriesSelected ? false : true">
                <v-icon left dark>fa-plus</v-icon>
                {{ $t('common.form.add-race') }}
            </v-btn>
        </template>
        <v-card class="borderDialog">
            <v-card-title>
                <span class="headline">{{ !bandEdit ? $t('page.series.add-race') : $t('page.series.edit-race')
                }}</span>
            </v-card-title>
        <v-card-text>
            <v-form ref="formSR" class="pt-4 inputSeries">
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete 
                            v-model="selectedRace"
                            :items="selectRaceItems"
                            :label="$t('page.series.table.race.label-select-race-creation')"
                            :rules="[ v => !!v || $t('page.series.race-required') ]"
                            :disabled="bandEdit"
                            outlined dense 
                            :no-data-text="$t('common.no_data')"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row v-if="bandEdit">
                    <v-col cols="12">
                        <v-text-field
                            outlined dense 
                            :label="$t('page.series.race-name')" 
                            v-model="raceName" 
                            :rules="[
                                (v) => $inputRules({value: v, minSize: 3, maxSize: 255, trim: true}, 'fieldLength') || $t('page.series.rule-raceName')
                            ]" 
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-text-field 
                            type="number" 
                            outlined dense 
                            :label="$t('common.table.race-order')"
                            v-model="raceOrder" 
                            :rules="[
                                (v) => $inputRules({value: v}, 'integer') || $t('page.series.rule-raceOrder')
                            ]"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-text-field type="number" outlined dense :label="$t('common.table.bonus-points')"
                            v-model="bonusPoints" :rules="[
                            (v) => $inputRules({value: v}, 'integer') || $t('page.series.error-bonus-points-invalid')
                            ]"></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-text-field type="number" outlined dense :label="$tc('common.table.point-multiplier')"
                            v-model="pointsMultiplier" :min="1" :rules="[
                            (v) => $inputRules({value: v}, 'integer') || $t('page.series.error-point-multiplier-invalid'),
                            (v) => v > 0 || $t('page.series.rule-point-multiplier-greater-than-zero')
                            ]" :hint="$tc('page.series.point-multiplier-hint')" persistent-hint></v-text-field>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" small text @click="close">{{ $t('common.form.cancel') }}</v-btn>
            <v-btn color="blue darken-1" small text @click="save">{{ !bandEdit ? $t('common.form.create') :
            $t('common.form.save') }}</v-btn>
        </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import seriesRaceService from '@/services/seriesRaceService';

export default {
    props: {
        dialog: Boolean, 
        bandEdit: Boolean,
        reopenEdition: Boolean
    },
    data() {
        return {
            selectRaceItems: [],
            selectedRace: undefined,
            raceOrder: undefined,
            bonusPoints: undefined,
            raceName: undefined,
            raceIdToRaceName: {},
            pointsMultiplier: 1
        };
    },
    computed: {
        ...mapState({
            seriesSelected: state => state.series.seriesSelected,
        }), 
        ...mapGetters({
            races: 'getRacesList',
            bSeriesResults: 'getBSeriesResults',
            raceSelected: 'getRaceSelected'
        }),
        dialogShow: {
            get() {
                return this.dialog;
            },
            set(newValue) {
                this.$emit('change', { index: 'dialog', value: newValue });
            }
        }
    },
    methods: {
        async openDialog() {
            this.$emit('change',{ index: 'bandEdit', value: false }); 
            await this.loadSelectInputRaces();
        },
        async loadSelectInputRaces(changeValue = false) {
            // Fetch races using the service
            const racesResponse = await seriesRaceService.getRacesSelect();

            // Check if there was an error and show an alert message
            if (racesResponse.error) {
                this.$emit('change', { index: 'alertMessage', value: this.$t('common.failed-load') });
                this.$emit('change', { index: 'dialogAlert', value: true });
                return;
            }

            // Populate raceIdToRaceName map for later use
            racesResponse.forEach((element) => {
                this.raceIdToRaceName[element.raceId] = element.raceName;
            });

            // Populate selectRaceItems with formatted data
            this.selectRaceItems = racesResponse.map(element => ({
                value: element.raceId,
                text: `${element.raceId} - ${this.$escapeHtml.decode(element.raceName)} :: ${element.eventId} - ${this.$escapeHtml.decode(element.eventName)}`
            }));

            // Optionally update selected race
            if (changeValue) {
                this.selectedRace = changeValue;
            }

            // Reset form if it exists
            this.$refs.formAddToExistingRace?.reset();
        },

        close() {
            this.dialogShow = false;
            const parent = this;
            setTimeout(function() {
                if(parent.bandEdit && !parent.reopenEdition) {
                    parent.$emit('change',{ index: 'bandEdit', value: false });
                }
            }, 1000);
            this.$nextTick(() => {
                this.$emit('change',{ index: 'editedItem', value: Object.assign({}, this.defaultItem) });
                this.$emit('change',{ index: 'editedIndex', value: -1 });
            });
        },

        /**
         * Saves race data using the external API service.
         */
        async save() {
            // Validate the form; return early if validation fails
            if (!this.$refs.formSR.validate()) return;

            // Call the API service to save the race with inline data
            const races = await seriesRaceService.saveRace({
                seriesId: this.seriesSelected.id,
                raceId: this.selectedRace,
                raceOrder: this.raceOrder,
                bonusPoints: this.bonusPoints,
                pointMultiplier: this.pointsMultiplier,
                raceName: this.bandEdit ? this.raceName : this.raceIdToRaceName[this.selectedRace],
                id: this.bandEdit && this.selectedItem ? this.selectedItem.id : null
            });

            // Handle errors if the response indicates failure
            if (!races || !races.success || races.error) {
                this.handleSaveError(races);
                return;
            }

            // Handle successful response
            this.$store.dispatch("importScoresAction", { raceId: this.selectedRace });
            if (!this.bandEdit && this.bSeriesResults && this.raceSelected === -1) {
                this.$store.dispatch('getResultsAction', { seriesId: this.seriesSelected.id });
            }
            this.$emit('loadRaces');
            this.$emit('change', { index: 'reopenEdition', value: false });
            this.close();
        },

        /**
         * Handles the error response specifically for the save function.
         *
         * Sets the dialog state and displays an appropriate error message based on the type of error received.
         *
         * @param {Object} races - The response object from the save API, which may contain an error message.
         */
        handleSaveError(races) {
            // Update dialog state for error handling
            this.dialogShow = false;
            this.$emit('change', { index: 'indexDialog', value: 'dialog' });
            this.$emit('change', { index: 'dialogAlert', value: true });
            this.$emit('change', { index: 'reopenEdition', value: true });

            // Set the alert message based on the error response
            const alertMessage = this.buildSaveAlertMessage(races);
            this.$emit('change', { index: 'alertMessage', value: alertMessage });
        },

        /**
         * Constructs an alert message specifically for errors in the save API response.
         *
         * @param {Object} races - The response object from the save API.
         * @returns {string} - The formatted alert message for display.
         */
        buildSaveAlertMessage(races) {
            if (!races || !races.error) {
                return this.$t('common.failed-save');
            }

            if (Array.isArray(races.error) && races.error.length > 0) {
                return this.$t('page.series.' + races.error[0]);
            }

            if (typeof races.error === 'string') {
                return this.$t('page.series.' + races.error);
            }

            return this.$t('common.failed-save');
        },

        /**
         * Prepares the dialog and form fields for editing a specific race item.
         *
         * This function loads the necessary race input data, then sets the dialog to visible
         * and populates the form fields with the selected item's details.
         *
         * @param {Object} item - The item object containing race details to be edited.
         */
        async editItem(item) {
            // Ensure item is defined to avoid errors
            if (!item) return;

            // Load selectable input races based on the given race ID
            await this.loadSelectInputRaces(item.raceId);

            // Set dialog to show and populate form fields with item details
            this.dialogShow = true;
            this.selectedRace = parseInt(item.raceId, 10);
            this.raceOrder = item.raceOrder;
            this.bonusPoints = item.bonusPoints;
            this.selectedItem = item;
            this.raceName = item.raceName;
            this.pointsMultiplier = item.pointsMultiplier;
        }

    },
    watch: {
        dialog(val) {
            if (!val) {
                this.close();
                return;
            }

            // Reset validation for the form if it exists
            if (this.$refs.formSR) {
                this.$refs.formSR.resetValidation();
            }

            // Calculate the next race order if not in editing mode
            if (!this.bandEdit) {
                const nums = this.races
                    .map((el) => parseInt(el.raceOrder))
                    .filter((el) => !isNaN(el));

                this.raceOrder = nums.length > 0 ? Math.max(...nums) + 1 : 1;
                this.selectedRace = null;
                this.bonusPoints = 0;
                this.pointsMultiplier = 1;
                this.selectedItem = null;
                this.raceName = null;
            }
        },
    }

};
</script>

<style>
</style>